import { call, put, select } from 'redux-saga/effects';

import {
  HYDRATE,
  type HydrateAction,
} from '@/domains/core/applicationState/store/hydrateAction';
import { fetchMainReviews } from '@/domains/productDiscovery/ArticleReviews/articleReviews.api';
import type {
  MainReviewsState,
  Review,
} from '@/domains/productDiscovery/ArticleReviews/ArticleReviews.model';
import { SpartacuxProductDiscoveryDomainState } from '@/domains/productDiscovery/productDiscovery.model';
import {
  GET_DETAIL_SUCCESS,
  GRAPHQL_SUCCESS,
  GraphqlSuccess,
  type GetDetailSuccess,
} from '@/domains/productDiscovery/ProductPage/detail.actions';

export const GET_MAIN_REVIEWS = 'product/GET_MAIN_REVIEWS';

export interface GetLatestReviews {
  type: typeof GET_MAIN_REVIEWS;
  articleId: number;
}

export const GET_MAIN_REVIEWS_SUCCESS = 'product/GET_MAIN_REVIEWS_SUCCESS';

export interface GetLatestReviewsSuccess {
  type: typeof GET_MAIN_REVIEWS_SUCCESS;
  latestReviews: Review[];
  bestRatedReviews: Review[];
  total: number;
}

export const GET_MAIN_REVIEWS_FAILURE = 'product/GET_MAIN_REVIEWS_FAILURE';

export interface GetLatestReviewsFailure {
  type: typeof GET_MAIN_REVIEWS_FAILURE;
  error: string;
}

export type Action =
  | GetLatestReviews
  | GetLatestReviewsSuccess
  | GetLatestReviewsFailure
  | GetDetailSuccess
  | GraphqlSuccess
  | HydrateAction;

export const initialState: MainReviewsState = {
  fetching: false,
  fetched: false,
  error: null,
  articleId: null,
  hasMoreReviews: false,
  total: 0,
  reviews: [],
  bestRatedReviews: [],
  latestReviews: [],
};

// SELECTORS
export const mainReviewsSelector = (
  state: SpartacuxProductDiscoveryDomainState,
) => state?.productDiscovery?.articleReviews?.mainReviews || initialState;

// REDUCER
export default function reducer(
  state: MainReviewsState = initialState,
  action: Action,
): MainReviewsState {
  switch (action.type) {
    case GET_MAIN_REVIEWS:
      return {
        reviews: [],
        bestRatedReviews: [],
        latestReviews: [],
        articleId: action.articleId,
        total: 0,
        hasMoreReviews: false,
        fetched: false,
        fetching: true,
        error: null,
      };

    case GET_MAIN_REVIEWS_SUCCESS: {
      const reviews = action.bestRatedReviews?.length
        ? action.bestRatedReviews
        : action.latestReviews;
      return {
        ...state,
        reviews,
        bestRatedReviews: action.bestRatedReviews,
        latestReviews: action.latestReviews,
        total: action.total,
        hasMoreReviews: action.total > reviews.length,
        fetching: false,
        fetched: true,
        error: null,
      };
    }

    case GET_MAIN_REVIEWS_FAILURE:
      return {
        ...state,
        reviews: [],
        bestRatedReviews: [],
        latestReviews: [],
        total: 0,
        hasMoreReviews: false,
        error: action.error,
        fetching: false,
        fetched: true,
      };

    case GET_DETAIL_SUCCESS: {
      if (action.detail.latestReviews) {
        const reviews = action.detail.latestReviews?.bestRatedReviews?.length
          ? action.detail.latestReviews.bestRatedReviews
          : action.detail.latestReviews.latestReviews;

        return {
          reviews,
          bestRatedReviews: action.detail.latestReviews.bestRatedReviews || [],
          latestReviews: action.detail.latestReviews.latestReviews,
          total: action.detail.latestReviews.totalReviewCount,
          hasMoreReviews:
            action.detail.latestReviews.totalReviewCount > reviews.length,
          articleId: action.detail.articleId,
          fetching: false,
          fetched: true,
          error: null,
        };
      }
      return initialState;
    }

    case GRAPHQL_SUCCESS: {
      const { payload } = action;
      const latestReviews = payload?.latestReviews;

      if (!latestReviews?.bestRatedReviews?.length)
        return {
          ...initialState,
          fetched: true,
        };

      const mappedReviews = latestReviews.bestRatedReviews
        .filter((review) => !!review)
        .map((review) => {
          // TODO: Clean this interface and this mapper when the 'productPageGraphQL' AB is finished
          return {
            incentivized: false,
            createdAt: review.createdAt,
            platform: '',
            title: '',
            customerName: '',
            isB2b: review.isB2b,
            isTranslated: false,
            message: review.message,
            pictures: [],
            purchaseDate: review.purchaseDate,
            rating: review.rating,
            reviewer: review.reviewer,
            reviewId: review.id,
            source: review.source,
            votes: review.totalVotes,
          } as Review;
        });

      // TODO: Clean this interface when the 'productPageGraphQL' AB is finished
      return {
        reviews: mappedReviews,
        bestRatedReviews: [],
        latestReviews: [],
        total: latestReviews.totalReviewCount || 0,
        hasMoreReviews:
          latestReviews.totalReviewCount >
          latestReviews.bestRatedReviews.length,
        articleId: latestReviews.articleId,
        fetching: false,
        fetched: true,
        error: null,
      };
    }

    case HYDRATE:
      return mainReviewsSelector(action.state);

    default:
      return state;
  }
}

// SAGA
interface actionWithArticleId {
  type: string;
  articleId: number;
}

export function* getMainReviewsSaga(
  action: actionWithArticleId,
): Generator<any, any, any> {
  try {
    const mainReviewsState = yield select(mainReviewsSelector);
    if (
      mainReviewsState.fetched &&
      mainReviewsState.articleId === action.articleId
    )
      return null;

    yield put({
      type: GET_MAIN_REVIEWS,
      articleId: action.articleId,
    });
    const { latestReviews, bestRatedReviews, total } = yield call(
      fetchMainReviews,
      action.articleId,
    );

    yield put({
      type: GET_MAIN_REVIEWS_SUCCESS,
      latestReviews,
      bestRatedReviews,
      total,
    });
  } catch (error) {
    yield put({
      type: GET_MAIN_REVIEWS_FAILURE,
      error: error.message,
    });
  }
}
